import { cx } from '@linaria/core';
import { useCallback, useRef } from 'react';
import { AutoFocusInside } from 'react-focus-on';
import { useNotifier, useModalContentManager } from '@provider/notifier';
import { Pill } from '@ui/kit';
import { PartialSmileyIcon, IncorrectSmileyIcon, CorrectSmileyIcon, RetryRotatedIcon } from '@ui/kit/icons';
import { getTaskEntryState } from '../../utils/get-task-entry-state.js';
import { useOpgavekompleksetComponent } from '../context.data.js';
import { useCurrentTaskEntryId, useCurrentTaskEntry, useOpgavekompleksetEntry } from '../hook.entry.js';
import { useAttemptMutation } from '../mutations/use-attempt.js';
import { makeSelectHasUninteractedTasks } from '../selectors/entry.has-uninteracted-tasks.js';
import { shouldShowFeedback } from '../util.should-show-feedback.js';
import { useGoToNextTaskAction } from './task.go-to-next.js';
import { modal, errorCount, smileyWrapper, textWrapper, heading, pill, remainingAttempts } from './task.validate-current.style.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

function useValidateCurrentTaskAction(args) {
  const component = useOpgavekompleksetComponent();
  const notifier = useNotifier();
  const {
    allowContinueOnClose = true
  } = {};
  const activeTaskId = useCurrentTaskEntryId();
  const attempts = useCurrentTaskEntry({
    select: entry => entry?.state.attempts ?? 0
  }) ?? 0;
  const stats = useCurrentTaskEntry({
    select: entry => entry?.stats
  });
  const hasUninteractedTasks = useOpgavekompleksetEntry({
    select: makeSelectHasUninteractedTasks(component.data, activeTaskId)
  });
  const maxAttempts = component.data.settings.allowedAttempts;
  const makeAttempt = useAttemptMutation();
  const goToNextTaskAction = useGoToNextTaskAction({
    skipFinishModal: true
  });
  return useCallback(async () => {
    if (!activeTaskId) {
      return;
    }
    const currentTaskIndex = component.data.tasks.findIndex(it => it.id === activeTaskId);
    const nextTaskIndex = currentTaskIndex + 1;
    const nextTask = component.data.tasks[nextTaskIndex];
    const nextAttempt = attempts + 1;
    const state = getTaskEntryState(stats);
    const action = (() => {
      if (nextAttempt < maxAttempts && state !== "correct") {
        return "retry";
      }
      if (state === "correct") {
        // In case we're at the final task, but there's tasks before (in free
        // progression) that haven't been answered, then we should not go to the
        // results page, but instead to facit to allow users to finalize
        // remaining tasks.
        if (!nextTask && hasUninteractedTasks) {
          return "facit";
        }
        return "next";
      }
      return shouldShowFeedback(component.data.settings.flowType) ? "facit" : "next";
    })();
    await notifier.modal(makeAttemptModalConfig({
      state,
      action,
      // NB: In case we're at the end, but there's uninteracted tasks, then
      // the button label should indicate this.
      facitType: !nextTask && hasUninteractedTasks ? "close" : "view",
      // NB: On the final task, if all tasks have been interacted with, then
      // we should go directly to the results screen.
      nextType: !nextTask && !hasUninteractedTasks ? "results" : "next",
      remainingAttempts: maxAttempts - nextAttempt,
      amountOfWrongAnswers: stats?.wrong ?? 0,
      forceCloseOnClick: !allowContinueOnClose
    }));
    makeAttempt(activeTaskId);
    if (allowContinueOnClose && action === "next") {
      goToNextTaskAction();
    }
  }, [activeTaskId, allowContinueOnClose, attempts, component.data.settings.flowType, component.data.tasks, goToNextTaskAction, hasUninteractedTasks, makeAttempt, maxAttempts, notifier, stats]);
}
const makeAttemptModalConfig = args => {
  return {
    className: cx(modal, args.state),
    content: /*#__PURE__*/jsx(ValidationModal, {
      ...args
    }),
    requiresAction: args.action !== "retry",
    theme: "dialog"
  };
};
function ValidationModal(props) {
  const lottieRef = useRef(null);
  const buttonRef = useRef(null);
  const modal = useModalContentManager();
  const heading$1 = (() => {
    switch (props.state) {
      case "correct":
        return getMessage("correct");
      case "incorrect":
        return getMessage("wrong");
      case "partial":
        return /*#__PURE__*/jsxs(Fragment, {
          children: [/*#__PURE__*/jsxs("span", {
            className: errorCount,
            children: [props.amountOfWrongAnswers, " fejl"]
          }), /*#__PURE__*/jsx("span", {
            children: getMessage("partial")
          })]
        });
    }
  })();
  const buttonLabel = (() => {
    if (props.forceCloseOnClick) {
      return "Luk";
    }
    switch (props.action) {
      case "facit":
        return props.facitType === "close" ? "Luk" : "Se rigtigt svar";
      case "next":
        return props.nextType === "results" ? "Se resultat" : "Gå til næste";
      case "retry":
        return "Prøv igen";
    }
  })();
  const smiley = (() => {
    switch (props.state) {
      case "correct":
        return /*#__PURE__*/jsx(CorrectSmileyIcon, {
          lottieRef: lottieRef
        });
      case "incorrect":
        return /*#__PURE__*/jsx(IncorrectSmileyIcon, {
          lottieRef: lottieRef
        });
      case "partial":
        return /*#__PURE__*/jsx(PartialSmileyIcon, {
          lottieRef: lottieRef
        });
    }
  })();
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("div", {
      className: smileyWrapper,
      children: smiley
    }), /*#__PURE__*/jsxs("div", {
      className: textWrapper,
      children: [/*#__PURE__*/jsx("h4", {
        className: heading,
        children: heading$1
      }), /*#__PURE__*/jsx(AutoFocusInside, {
        children: /*#__PURE__*/jsx(Pill, {
          ref: buttonRef,
          size: "compact",
          className: pill,
          icon: props.action === "retry" && !props.forceCloseOnClick ? /*#__PURE__*/jsx(RetryRotatedIcon, {}) : undefined,
          onClick: () => modal.onDismiss(),
          onPointerEnter: () => {
            switch (props.state) {
              case "correct":
                CorrectSmileyIcon.blink(lottieRef.current);
                break;
              case "partial":
                PartialSmileyIcon.blink(lottieRef.current);
                break;
              case "incorrect":
                IncorrectSmileyIcon.blink(lottieRef.current);
                break;
            }
          },
          children: buttonLabel
        })
      }), props.action === "retry" && !props.forceCloseOnClick && /*#__PURE__*/jsxs("span", {
        className: remainingAttempts,
        children: ["Du har ", /*#__PURE__*/jsx("b", {
          children: props.remainingAttempts
        }), " fors\xF8g tilbage"]
      })]
    })]
  });
}
const MESSAGES = {
  correct: [{
    msg: "Helt rigtigt, godt gået!",
    weight: 0.334
  }, {
    msg: "Rigtigt, godt arbejde!",
    weight: 0.333
  }, {
    msg: "Godt klaret, det er rigtigt!",
    weight: 0.33
  }],
  partial: [{
    msg: "Det er ikke helt rigtigt.",
    weight: 0.75
  }, {
    msg: "Noget er rigtigt, noget er forkert.",
    weight: 0.25
  }],
  wrong: [{
    msg: "Det var ikke rigtigt.",
    weight: 0.75
  }, {
    msg: "Det var forkert.",
    weight: 0.25
  }]
};
function getMessage(type) {
  const rnd = Math.random();
  let message = "";
  let accumulatedWeight = 0;
  for (const option of MESSAGES[type]) {
    message = option.msg;
    accumulatedWeight += option.weight;
    if (accumulatedWeight >= rnd) {
      break;
    }
  }
  return message;
}

export { ValidationModal, makeAttemptModalConfig, useValidateCurrentTaskAction };
//# sourceMappingURL=task.validate-current.js.map
